/**
 * phaserPerfSource — PerfSource Port의 Phaser/브라우저 어댑터.
 *
 * fps·frameMs는 Phaser game.loop, heapMB는 performance.memory(Chromium 전용 — 미지원이면 null),
 * draw call은 WebGLRenderer.drawCount(Canvas면 생략). 게임 도메인 객체 수(proj/brick/tween)는
 * 앱이 counts 콜백으로 주입한다 — 패키지가 게임 도메인을 알 필요 없게(DIP).
 */
import type Phaser from "phaser";
import type { PerfFrameSample, PerfSource } from "./types.js";

const BYTES_PER_MB = 1024 * 1024;

interface MemoryLike {
  usedJSHeapSize?: number;
  jsHeapSizeLimit?: number;
}

const finiteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

export function createPhaserPerfSource(
  game: Phaser.Game,
  counts: () => Record<string, number>,
): PerfSource {
  return {
    sample(): PerfFrameSample {
      const loop = game.loop;
      const mem = (performance as unknown as { memory?: MemoryLike }).memory;
      const heapMB =
        mem && typeof mem.usedJSHeapSize === "number" ? mem.usedJSHeapSize / BYTES_PER_MB : null;
      const heapLimitMB =
        mem && typeof mem.jsHeapSizeLimit === "number" ? mem.jsHeapSizeLimit / BYTES_PER_MB : null;

      const c = { ...counts() };
      const draws = (game.renderer as unknown as { drawCount?: number }).drawCount;
      if (typeof draws === "number") c.draw = draws;
      if (finiteNumber(loop.actualFps)) c.loopActualFps = Math.round(loop.actualFps);
      if (finiteNumber(loop.delta)) c.loopDelta = Math.round(loop.delta);
      if (finiteNumber(loop.rawDelta)) c.loopRawDelta = Math.round(loop.rawDelta);
      const targetFps = (loop as unknown as { targetFps?: number }).targetFps;
      if (finiteNumber(targetFps)) c.targetFps = Math.round(targetFps);

      // frameMs는 rawDelta(평활 전 실제 프레임 간격) — loop.delta는 smoothStep=true면 여러 프레임
      // 평균이라 렉 순간을 둔하게 보여준다. rawDelta로 렉을 즉시 포착(평활은 perfStats가 fpsAlpha로 1회만).
      const frameMs = loop.rawDelta > 0 ? loop.rawDelta : loop.delta;
      const fps = frameMs > 0 ? 1000 / frameMs : loop.actualFps;

      return {
        fps,
        frameMs,
        heapMB,
        heapLimitMB,
        counts: c,
      };
    },
  };
}
